import cn from "classnames";
import React, { FunctionComponent, useState } from "react";
import { CalculatorResults } from "../../components/CalculatorResults";
import { CalculatorHeading } from "../../components/panels/CalculatorPanels/calculatorHeading";
import { RichText } from "../../components/RichText";
import { ICalculatorTabInformation } from "../../types/ICalculatorTabInformation";
import { IContentfulRichText } from "../../types/IContentfulRichText";
import { SituationQuestions } from "./SituationQuestions";

import "./styles.scss";

export interface IExampleSavingsOption {
  criteria: string;
  id: string;
  cost: number;
  isCurrency: boolean;
  costSuffix: string;
  text: string;
}

export interface ISelectedExampleSavingsOption {
  id: string;
  checked: boolean;
}

export interface IProps {
  title: string;
  description: IContentfulRichText | string;
  disclaimer: IContentfulRichText | string;
  tabInformation: ICalculatorTabInformation[];
  defaultTab: number;
  onTabClick?: (index: number, amount?: string) => void;
  isAtTopOfPage?: boolean;
  showExampleSavings?: boolean;
  exampleSavingsOptions?: IExampleSavingsOption[];
  id: string;
}

export const getInitialSelectedExampleSavingsOptions = (
  savingsOptions: IExampleSavingsOption[],
): ISelectedExampleSavingsOption[] =>
  savingsOptions.map((savingsOption) => ({
    id: savingsOption.id,
    checked: false,
  }));

export const CalculatorTemplate: FunctionComponent<IProps> = (props) => {
  const {
    title,
    description,
    disclaimer,
    tabInformation,
    defaultTab,
    onTabClick,
    isAtTopOfPage,
    showExampleSavings,
    exampleSavingsOptions,
    id,
  } = props;
  const [currentTabIndex, setCurrentTabIndex] = useState(
    defaultTab ? defaultTab - 1 : 0,
  );

  const initialSelectedExampleSavingsOptions = exampleSavingsOptions
    && getInitialSelectedExampleSavingsOptions(exampleSavingsOptions);

  const [
    selectedExampleSavingsOptions,
    setSelectedExampleSavingsOptions,
  ] = useState<ISelectedExampleSavingsOption[] | undefined>(
    initialSelectedExampleSavingsOptions,
  );
  const multipleTabs = tabInformation.length > 1;
  const formAreaClasses = cn("form-area", {
    addMarginTop: !multipleTabs,
  });

  const filteredExampleSavingsOptions = exampleSavingsOptions
    && exampleSavingsOptions.filter((option) => {
      const matchingSavingsOption = selectedExampleSavingsOptions
        && selectedExampleSavingsOptions.find(
          (selectedOption) => selectedOption.id === option.id,
        );
      return !!matchingSavingsOption?.checked;
    });

  return (
    <section className="calculator" id={id}>
      <div className="container">
        <div
          className="calculator-content"
          role="region"
          aria-label="Calculator content"
        >
          <CalculatorHeading
            isAtTopOfPage={isAtTopOfPage}
            dataTestId="title"
            className="calculator-heading"
          >
            {title}
          </CalculatorHeading>
          <div data-testid="description" className="calculator-description">
            {typeof description === "string" ? (
              description
            ) : (
              <RichText
                className="calculator-description__content"
                content={description}
              />
            )}
          </div>
          {multipleTabs && (
            <div className="tabs">
              {tabInformation.map((tab, index) => (
                <div
                  role="button"
                  tabIndex={0}
                  key={index}
                  className={cn("tab", { active: index === currentTabIndex })}
                  onClick={() => {
                    setCurrentTabIndex(index);
                    if (onTabClick) {
                      onTabClick(
                        index,
                        index === 1
                          ? tabInformation[0].calculatorResults.top.result.toString()
                          : undefined,
                      );
                    }
                  }}
                  onKeyPress={() => {
                    setCurrentTabIndex(index);
                    if (onTabClick) {
                      onTabClick(
                        index,
                        index === 1
                          ? tabInformation[0].calculatorResults.top.result.toString()
                          : undefined,
                      );
                    }
                  }}
                >
                  {tab.title}
                </div>
              ))}
            </div>
          )}

          <div className={formAreaClasses}>
            <div className="form">{tabInformation[currentTabIndex].form}</div>
            {showExampleSavings
              && exampleSavingsOptions
              && selectedExampleSavingsOptions && (
                <SituationQuestions
                  selectedExampleSavingsOptions={selectedExampleSavingsOptions}
                  setSelectedExampleSavingsOptions={setSelectedExampleSavingsOptions}
                  exampleSavingsOptions={exampleSavingsOptions}
                />
            )}
          </div>
          <div className="calculator-disclaimer hide__mobile">
            {typeof disclaimer === "string" ? (
              disclaimer
            ) : (
              <RichText
                className="calculator-disclaimer desktop"
                content={disclaimer}
              />
            )}
          </div>
        </div>
        <CalculatorResults
          {...tabInformation[currentTabIndex].calculatorResults}
          showExampleSavings={showExampleSavings}
          exampleSavingsOptions={filteredExampleSavingsOptions}
        />
        <p
          className="calculator-disclaimer hide__desktop"
          role="region"
          aria-label="Calculator disclaimer"
        >
          {typeof disclaimer === "string" ? (
            disclaimer
          ) : (
            <RichText className="calculator-disclaimer mobile" content={disclaimer} />
          )}
        </p>
      </div>
    </section>
  );
};
